import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Loader2, Plus, RefreshCw, Copy, Check } from 'lucide-react';
import { useMySchoolMembership, useSchoolCohorts, useSchoolPrograms, useManageSchool, useRotateJoinCode } from '@/hooks/useSchoolApi';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/components/ui/use-toast';

export default function CohortsManager() {
  const { user } = useAuth();
  const { data: membership } = useMySchoolMembership();
  const schoolId = (membership as any)?.schools?.id || membership?.school_id;
  const { data: cohorts, isLoading } = useSchoolCohorts(schoolId);
  const { data: programs } = useSchoolPrograms(schoolId);
  const manageSchool = useManageSchool();
  const rotateCode = useRotateJoinCode();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [name, setName] = useState('');
  const [programId, setProgramId] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const isAdmin = membership?.role === 'school_admin';

  // Filter cohorts for instructors — only show their own
  const filteredCohorts = isAdmin
    ? cohorts
    : cohorts?.filter((c: any) => c.created_by === user?.id);

  const handleCreate = () => {
    if (!programId || !name.trim()) return;
    manageSchool.mutate(
      { action: 'create_cohort', programId, name: name.trim() },
      { onSuccess: () => { setDialogOpen(false); setName(''); setProgramId(''); } }
    );
  };

  const handleCopyCode = (code: string, cohortId: string) => {
    navigator.clipboard.writeText(code);
    setCopiedId(cohortId);
    toast({ title: 'Copied', description: `Join code ${code} copied to clipboard.` });
    setTimeout(() => setCopiedId(null), 2000);
  };

  if (isLoading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold tracking-tight">Cohorts</h2>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button size="sm"><Plus className="h-4 w-4 mr-1" /> New Cohort</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create Cohort</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div className="space-y-1.5">
                <Label>Program</Label>
                <Select value={programId} onValueChange={setProgramId}>
                  <SelectTrigger><SelectValue placeholder="Select program" /></SelectTrigger>
                  <SelectContent>
                    {programs?.map((p: any) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Name</Label>
                <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Fall 2026" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleCreate} disabled={!name.trim() || !programId || manageSchool.isPending}>
                {manageSchool.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {!filteredCohorts?.length ? (
        <p className="text-sm text-muted-foreground text-center py-8">No cohorts yet.</p>
      ) : (
        <div className="grid gap-3">
          {filteredCohorts.map((c: any) => (
            <Card key={c.id}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base">{c.name}</CardTitle>
                    <p className="text-xs text-muted-foreground">{c.school_programs?.name}</p>
                  </div>
                  <Badge variant={c.join_code_active ? 'default' : 'outline'}>
                    {c.join_code_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm bg-muted px-2 py-1 rounded">{c.join_code || 'N/A'}</span>
                  {c.join_code && (
                    <>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => handleCopyCode(c.join_code, c.id)}
                      >
                        {copiedId === c.id ? <Check className="h-3.5 w-3.5 text-primary" /> : <Copy className="h-3.5 w-3.5" />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        disabled={rotateCode.isPending}
                        onClick={() => rotateCode.mutate(c.id)}
                      >
                        <RefreshCw className={`h-3.5 w-3.5 ${rotateCode.isPending ? 'animate-spin' : ''}`} />
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
